import * as React from 'react'
import { INode, ILeaf } from 'shared/types/tree'
import {
  Box,
  Button,
  Table,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
} from '@mui/material'
import { Standard, StandardsMap } from 'shared/types'
import { TreeStandards } from 'components/tree/standardUtilities'
import { NodeIdMap, createIdMap } from '../version_mapping/utility'
import {
  OpenNodeButton,
  OpenNodeTKatBuilderButton,
} from '../tree_navigation/TreeNavigation'
import { deleteLeaves } from 'shared/api/tree'
import { ConfirmCommitButton } from 'shared/ui'
import { standardRequest } from 'shared/hooks/standards/hooks'

import { useUpdateLeafData } from 'shared/api/tree'
import { VerifyStandardsContext } from './VerifyStandardsContext'
import { useParams } from 'infrastructure/routing'

function ReplaceStandardButton({
  standardLeaf,
  setUpdated,
}: {
  readonly standardLeaf: ILeaf
  readonly setUpdated: (x: boolean) => void
}) {
  const updateLeaf = useUpdateLeafData()
  const [showOptions, setShowOptions] = React.useState<boolean>(false)
  const [options, setOptions] = React.useState<Standard[]>([])
  const [replacing, setReplacing] = React.useState<boolean>(false)

  const { standardsMap } = React.useContext(VerifyStandardsContext)
  const standard = standardsMap[standardLeaf.body.skillID]

  React.useEffect(() => {
    if (showOptions && options.length === 0) {
      standardRequest({ standard_name: standard.standardName }).then((s) => {
        setOptions(
          s.filter((e) => {
            return e.isCommonCore || e.state
          }),
        )
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOptions])

  const doUpdateLeaf = (s: Standard) => {
    return async () => {
      setReplacing(true)
      const { tree_id, version, id } = standardLeaf
      await updateLeaf({
        tree_id,
        version,
        id,
        body: { body: { skillID: s.standardId } },
      })
      setReplacing(false)
      setUpdated(true)
    }
  }
  return (
    <>
      <Button
        disabled={replacing}
        onClick={() => {
          setShowOptions((prev) => !prev)
        }}
      >
        {replacing ? 'Replacing...' : 'Replace Standard'}
      </Button>
      {showOptions && (
        <Box>
          {options.map((s) => {
            const append = s.state || 'CC'
            return (
              <MenuItem
                key={s.standardId}
                disabled={replacing}
                onClick={doUpdateLeaf(s)}
              >
                {s.standardName} ({append})
              </MenuItem>
            )
          })}
        </Box>
      )}
    </>
  )
}

function StandardRow({ standardLeaf }: { standardLeaf: ILeaf }) {
  const [deleting, setDeleting] = React.useState<boolean>(false)
  const [deleted, setDeleted] = React.useState<boolean>(false)
  const [updated, setUpdated] = React.useState<boolean>(false)
  const { standardsMap, idMap } = React.useContext(VerifyStandardsContext)

  if (deleted || updated) return <></>

  const standard = standardsMap[standardLeaf.body.skillID]

  return (
    <TableRow key={standardLeaf.id}>
      <TableCell>{standard.standardId}</TableCell>
      <TableCell>{standard.standardName}</TableCell>
      <TableCell>{standard.standardDescription}</TableCell>
      <TableCell>{standard.isCommonCore}</TableCell>
      <TableCell>{standard.state}</TableCell>
      <TableCell>{standard.luserId}</TableCell>
      <TableCell>
        <OpenNodeButton
          node={idMap[standardLeaf.parent_id!].node}
          idMap={idMap}
        />
      </TableCell>
      <TableCell>
        <Button
          disabled={deleting}
          onClick={() => {
            setDeleting(true)
            deleteLeaves(standardLeaf).then(() => {
              setDeleting(false)
              setDeleted(true)
            })
          }}
        >
          {deleting ? 'Removing Standard...' : 'Remove Standard'}
        </Button>
      </TableCell>
      <TableCell>
        <ReplaceStandardButton
          standardLeaf={standardLeaf}
          setUpdated={setUpdated}
        />
      </TableCell>
    </TableRow>
  )
}

function StandardTable({ leaves }: { readonly leaves: ILeaf[] }) {
  return (
    <Box>
      <Table>
        <TableHead>
          <TableCell>ID</TableCell>
          <TableCell>Name</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>IsCommonCore</TableCell>
          <TableCell>State</TableCell>
          <TableCell>LUserID</TableCell>
          <TableCell>Kiddom Link</TableCell>
        </TableHead>
        {leaves.map((standardLeaf: ILeaf) => {
          return (
            <StandardRow key={standardLeaf.id} standardLeaf={standardLeaf} />
          )
        })}
      </Table>
    </Box>
  )
}

function StandardsErrorsTable({
  leaves,
  treeId,
}: {
  leaves: ILeaf[]
  treeId: string
}) {
  const { standardsMap, idMap } = React.useContext(VerifyStandardsContext)

  return (
    <Box>
      <Table>
        <TableHead>
          <TableCell>ID</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Kiddom Link</TableCell>
          <TableCell>Open in Tkat Builder</TableCell>
        </TableHead>
        {leaves.map((standardLeaf: ILeaf) => {
          return (
            <TableRow key={standardLeaf.id}>
              <TableCell>{standardLeaf.body.skillID}</TableCell>
              <TableCell>{standardLeaf.type}</TableCell>
              <TableCell>
                <OpenNodeButton
                  node={idMap[standardLeaf.parent_id!].node}
                  idMap={idMap}
                />
              </TableCell>
              <TableCell>
                <OpenNodeTKatBuilderButton
                  node={idMap[standardLeaf.parent_id!].node}
                />
              </TableCell>
            </TableRow>
          )
        })}
      </Table>
    </Box>
  )
}

async function fetchTreeStandards(
  tree: INode | null | undefined,
  setLoadingStandardsInfo: (x: boolean) => void,
  setIdMap: (x: NodeIdMap) => void,
  setUserGeneratedStandardLeaves: (x: ILeaf[]) => void,
  setStandardsMap: (x: StandardsMap) => void,
  setErrors: (x: { [key: string]: string }) => void,
  setLeavesWithStandardErrors: (x: ILeaf[]) => void,
) {
  if (!tree) return
  setLoadingStandardsInfo(true)
  const treeStandards = await TreeStandards.create(tree)
  setIdMap(createIdMap(tree))

  const fetchedLeaves = await treeStandards.userGeneratedLeaves
  const fetchedStandardsMap = await treeStandards.standardsMap

  setUserGeneratedStandardLeaves(fetchedLeaves)
  setStandardsMap(fetchedStandardsMap)
  setLoadingStandardsInfo(false)
  setErrors(await treeStandards.getStandardsErrors())
  setLeavesWithStandardErrors(await treeStandards.getLeavesWithStandardErrors())
}

export function VerifyStandards({
  tree,
  reFetchTree,
}: {
  tree: INode
  reFetchTree: () => void
}) {
  const { treeId: currentMasterId } = useParams()

  const [idMap, setIdMap] = React.useState<NodeIdMap>({})

  const [showUserGeneratedStandards, setShowUserGeneratedStandards] =
    React.useState<boolean>(false)

  const [userGeneratedStandardLeaves, setUserGeneratedStandardLeaves] =
    React.useState<ILeaf[]>([])

  const [deletingUserGenerated, setDeletingUserGenerated] =
    React.useState<boolean>(false)

  const [loadingStandardsInfo, setLoadingStandardsInfo] =
    React.useState<boolean>(false)

  const [errors, setErrors] = React.useState<{ [key: string]: string }>({})

  const [leavesWithStandardErrors, setLeavesWithStandardErrors] =
    React.useState<ILeaf[]>([])

  const [standardsMap, setStandardsMap] = React.useState<StandardsMap>({})

  React.useEffect(() => {
    const fetchData = async () => {
      await fetchTreeStandards(
        tree,
        setLoadingStandardsInfo,
        setIdMap,
        setUserGeneratedStandardLeaves,
        setStandardsMap,
        setErrors,
        setLeavesWithStandardErrors,
      )
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree])

  function ShowHideUserGeneratedStandardsButton() {
    return (
      <Button
        onClick={() => {
          setShowUserGeneratedStandards((prev) => !prev)
        }}
      >
        {showUserGeneratedStandards
          ? 'Hide User Generated Standards'
          : 'Show User Generated Standards'}
      </Button>
    )
  }

  const contextValue = React.useMemo(() => {
    return {
      standardsMap,
      idMap,
    }
  }, [standardsMap, idMap])

  if (!tree) {
    return null
  }

  if (loadingStandardsInfo) {
    return <Box>Loading...</Box>
  }

  return (
    <VerifyStandardsContext.Provider value={contextValue}>
      <Box>
        This tree has {userGeneratedStandardLeaves.length} user generated
        standards.
        {userGeneratedStandardLeaves.length > 0 && (
          <Box>
            <ShowHideUserGeneratedStandardsButton />
            <ConfirmCommitButton
              disabled={deletingUserGenerated}
              onClick={() => {
                setDeletingUserGenerated(true)
                deleteLeaves(...userGeneratedStandardLeaves)
                  .then(() => {
                    return reFetchTree()
                  })
                  .then(() => {
                    setDeletingUserGenerated(false)
                  })
              }}
            >
              {deletingUserGenerated
                ? 'Removing...'
                : 'Remove All User Generated Standards'}
            </ConfirmCommitButton>

            {showUserGeneratedStandards && (
              <StandardTable leaves={userGeneratedStandardLeaves} />
            )}
          </Box>
        )}
        {errors && (
          <>
            <br />
            <Box>Orphaned Standards:</Box>
            <Box>
              {Object.keys(errors).map((key) => {
                return <Box key={key}>{key}</Box>
              })}
            </Box>
            <br />
          </>
        )}
        {leavesWithStandardErrors.length > 0 && (
          <>
            <Box>
              Nodes with Orphaned Standard: {leavesWithStandardErrors.length}
            </Box>
            <StandardsErrorsTable
              leaves={leavesWithStandardErrors}
              treeId={currentMasterId ?? ''}
            />
          </>
        )}
      </Box>
    </VerifyStandardsContext.Provider>
  )
}
